// @tennis-matches/services/trainerstunden/free-training-trainer
// System-Trainer „Freies Training“ — ohne Abrechnung, Standard in der Planung.

import { generateId } from "@tennis-matches/core/utils";
import { normalizeTrainerId } from "./trainer-assistant-honorar.js";

export const FREE_TRAINING_TRAINER_FIRST_NAME = "Freies";
export const FREE_TRAINING_TRAINER_LAST_NAME = "Training";

/**
 * @param {Record<string, unknown>|null|undefined} trainer
 * @returns {boolean}
 */
export function isFreeTrainingTrainer(trainer) {
  return Boolean(trainer?.is_free_training);
}

/**
 * System-Trainer oder Legacy-Dublette mit gleichem Anzeigenamen.
 * @param {Record<string, unknown>|null|undefined} trainer
 * @returns {boolean}
 */
export function matchesFreeTrainingTrainerIdentity(trainer) {
  if (isFreeTrainingTrainer(trainer)) return true;
  const fn = String(trainer?.first_name ?? "").trim();
  const ln = String(trainer?.last_name ?? "").trim();
  return (
    fn === FREE_TRAINING_TRAINER_FIRST_NAME &&
    ln === FREE_TRAINING_TRAINER_LAST_NAME
  );
}

/**
 * Platzhalter-Trainer (keine Abrechnung, nur zum Bündeln von Spielern).
 * @param {Record<string, unknown>|null|undefined} trainer
 * @returns {boolean}
 */
export function isGroupingPlaceholderTrainer(trainer) {
  if (isFreeTrainingTrainer(trainer)) return true;
  if (Boolean(trainer?.is_grouping_placeholder)) return true;
  return matchesFreeTrainingTrainerIdentity(trainer);
}

/**
 * Abrechenbarer, menschlicher Trainer (für Finanz-Filter und Rechnungssteller).
 * @param {Record<string, unknown>|null|undefined} trainer
 * @returns {boolean}
 */
export function isBillableHumanTrainer(trainer) {
  if (!trainer || trainer.is_inactive === true) return false;
  return !isGroupingPlaceholderTrainer(trainer);
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @returns {Array<Record<string, unknown>>}
 */
export function filterBillableHumanTrainers(trainers) {
  return (trainers || []).filter(isBillableHumanTrainer);
}

/**
 * Trainer, die im Eltern-/Spieler-Portal buchbar sind (ohne „Freies Training“).
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @returns {Array<Record<string, unknown>>}
 */
export function filterParentPortalBookableTrainers(trainers) {
  return (trainers || []).filter(
    (tr) => !matchesFreeTrainingTrainerIdentity(tr),
  );
}

/**
 * @param {Record<string, unknown>|null|undefined} trainer
 * @returns {string}
 */
export function resolveFreeTrainingFocus(trainer) {
  return String(trainer?.free_training_focus || "").trim();
}

/**
 * @param {string} [tenantId]
 * @returns {Record<string, unknown>}
 */
export function buildFreeTrainingTrainerSeed(tenantId = "") {
  return {
    id: generateId(12),
    tenant_id: tenantId || null,
    first_name: FREE_TRAINING_TRAINER_FIRST_NAME,
    last_name: FREE_TRAINING_TRAINER_LAST_NAME,
    is_free_training: true,
    is_assistant_only: false,
    is_inactive: false,
    free_training_focus: null,
    license_type: null,
    notes: null,
    login_email: null,
    login_role: "trainer",
    ui_permissions: {},
    availability: [],
    absences: [],
    standard_rate_periods: [],
    hourly_rates: [],
    billing_frequency: null,
    billing_payment_timing: null,
    payment_due_days: 7,
    can_access_schleifchen: false,
    can_manage_schleifchen: false,
  };
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @returns {Record<string, unknown>|null}
 */
export function findFreeTrainingTrainer(trainers) {
  return (trainers || []).find(isFreeTrainingTrainer) || null;
}

/**
 * Freies Training zuerst, danach alphabetisch.
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @returns {Array<Record<string, unknown>>}
 */
export function sortTrainersWithFreeTrainingFirst(trainers) {
  const list = Array.isArray(trainers) ? [...trainers] : [];
  list.sort((a, b) => {
    const af = isFreeTrainingTrainer(a) ? 0 : 1;
    const bf = isFreeTrainingTrainer(b) ? 0 : 1;
    if (af !== bf) return af - bf;
    const an = `${a?.last_name || ""} ${a?.first_name || ""}`
      .trim()
      .toLowerCase();
    const bn = `${b?.last_name || ""} ${b?.first_name || ""}`
      .trim()
      .toLowerCase();
    return an.localeCompare(bn, "de");
  });
  return list;
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @returns {string}
 */
export function firstPrimaryPlanningTrainerIdPreferringFreeTraining(trainers) {
  const list = (trainers || []).filter(
    (tr) => !tr?.is_inactive && !tr?.is_assistant_only,
  );
  const free = findFreeTrainingTrainer(list);
  if (free) return normalizeTrainerId(free.id);
  const tr = list[0];
  return tr ? normalizeTrainerId(tr.id) : "";
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @returns {boolean}
 */
export function trainersIncludeFreeTraining(trainers) {
  return Boolean(findFreeTrainingTrainer(trainers));
}
